import React from "react";

// Stable empty-array reference so the `ibos` default doesn't change identity on
// every render (which would needlessly recreate `fetchSuggestions`).
const EMPTY_IBOS = [];

// The IBO search can return more than one row for the same IBO (e.g. a joint
// account like "Jones, Tom & Helen" comes back once per contact, all sharing a
// single iboNumber). Collapse those to one suggestion per IBO so the type-ahead
// doesn't render confusing duplicate rows — and, crucially, so the list keys
// (ibo.iboNumber) stay unique and React doesn't drop/duplicate entries.
function dedupeByIbo(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const result = [];
  for (const ibo of list) {
    const key = ibo?.iboNumber;
    // Rows without an iboNumber can't be safely collapsed — keep them all.
    if (key === undefined || key === null || key === "") {
      result.push(ibo);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(ibo);
  }
  return result;
}

// How many results a search asks for, and how much each "show more" adds.
const PAGE_SIZE = 20;

/**
 * Accept either the current `{ items, total }` response or a bare array.
 *
 * The array form is what the endpoint returned before it reported a total, and
 * the web and API deploy separately — during a rollout one can be newer than
 * the other, and the search box should not break in that window.
 */
function normalizeResult(result) {
  if (Array.isArray(result)) return { items: result, total: result.length };
  const items = Array.isArray(result?.items) ? result.items : [];
  const total = Number.isFinite(result?.total) ? result.total : items.length;
  return { items, total };
}

/**
 * Drives the IBO type-ahead search box used in Create Order and Order History.
 *
 * Pass `searchFn` (an async function that receives the query string and a
 * result limit, and resolves to `{ items, total }`) to use the real/mock API.
 * The legacy `ibos` array prop is kept for backward compatibility — if
 * `searchFn` is omitted, suggestions are filtered synchronously from the array.
 */
export function useIboSearch({
  searchFn,
  ibos = EMPTY_IBOS,
  onSelect,
  onQueryReset,
  // Optional controlled query. When provided (e.g. lifted to a parent so the
  // Create Order and Order History tabs share one search box), these drive the
  // query instead of the hook's internal state. Omit them for standalone use.
  query: controlledQuery,
  setQuery: setControlledQuery,
} = {}) {
  const [internalQuery, setInternalQuery] = React.useState("");
  const isControlled =
    controlledQuery !== undefined && typeof setControlledQuery === "function";
  const query = isControlled ? controlledQuery : internalQuery;
  const setQuery = isControlled ? setControlledQuery : setInternalQuery;
  const [suggestions, setSuggestions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  // Total matching accounts, which can exceed what is on screen.
  const [totalCount, setTotalCount] = React.useState(0);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  // Monotonic request id so only the most recent lookup can update state. With
  // an async `searchFn` (the real API) rapid typing fires overlapping requests
  // whose responses can resolve out of order; without this guard a slower
  // earlier query could clobber a newer query's suggestions.
  const requestSeqRef = React.useRef(0);
  // The term and limit currently on screen, so "show more" can re-ask for the
  // same search with a bigger page.
  const lastTermRef = React.useRef("");
  const limitRef = React.useRef(PAGE_SIZE);

  const runSearch = React.useCallback(
    async (normalized, limit, { append }) => {
      const requestId = ++requestSeqRef.current;

      if (!normalized) {
        setSuggestions([]);
        setTotalCount(0);
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      if (append) setIsLoadingMore(true);
      else setIsLoading(true);

      try {
        let result;
        if (searchFn) {
          result = await searchFn(normalized, limit);
        } else {
          result = ibos.filter(
            ({
              name = "",
              iboNumber = "",
              email = "",
              phone = "",
              searchLabel = "",
            }) => {
              const haystack =
                `${name} ${iboNumber} ${email} ${phone} ${searchLabel}`.toLowerCase();
              return haystack.includes(normalized);
            }
          );
        }
        if (requestId !== requestSeqRef.current) return;
        const { items, total } = normalizeResult(result);
        const deduped = dedupeByIbo(items);
        setSuggestions(deduped);
        // A page can dedupe down to fewer rows than the server counted, but it
        // can never hold more than exist; never claim a total below what shows.
        setTotalCount(Math.max(total, deduped.length));
      } catch {
        if (requestId !== requestSeqRef.current) return;
        if (!append) {
          setSuggestions([]);
          setTotalCount(0);
        }
      } finally {
        if (requestId === requestSeqRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [searchFn, ibos]
  );

  const fetchSuggestions = React.useCallback(
    (value = "") => {
      const normalized = value.trim().toLowerCase();
      lastTermRef.current = normalized;
      // A new term starts at the first page again.
      limitRef.current = PAGE_SIZE;
      return runSearch(normalized, PAGE_SIZE, { append: false });
    },
    [runSearch]
  );

  /**
   * Widen the current search by one page.
   *
   * Re-requests the same term with a larger limit rather than fetching an
   * offset slice: results are de-duplicated to one row per IBO on both sides,
   * so an offset would skip accounts whenever a page boundary fell inside a
   * multi-contact account. A larger limit is always a superset.
   */
  const loadMore = React.useCallback(() => {
    const term = lastTermRef.current;
    if (!term) return undefined;
    limitRef.current += PAGE_SIZE;
    return runSearch(term, limitRef.current, { append: true });
  }, [runSearch]);

  const handleQueryChange = React.useCallback(
    (event) => {
      const nextValue = event?.target?.value ?? "";
      setQuery(nextValue);

      if (onQueryReset) {
        onQueryReset();
      }
    },
    [onQueryReset, setQuery]
  );

  const handleSelectIbo = React.useCallback(
    (ibo) => {
      if (!ibo) {
        return;
      }

      // Clear the search box once an IBO is chosen; the selected IBO is shown
      // in the header below, and the query is reset on tab switch anyway.
      setQuery("");
      if (onSelect) {
        onSelect(ibo);
      }
    },
    [onSelect, setQuery]
  );

  const resetSearch = React.useCallback(() => {
    setQuery("");
    setSuggestions([]);
    setTotalCount(0);
    setIsLoading(false);
    setIsLoadingMore(false);
    lastTermRef.current = "";
    limitRef.current = PAGE_SIZE;
  }, [setQuery]);

  return {
    query,
    suggestions,
    isLoading,
    totalCount,
    isLoadingMore,
    hasMore: suggestions.length < totalCount,
    loadMore,
    fetchSuggestions,
    handleQueryChange,
    handleSelectIbo,
    resetSearch,
  };
}

export default useIboSearch;
